import React, { useState } from "react";
import { publishBlocker } from "./riskScoreDisplay";

/**
 * 14031 — Admin publish bar for the Questionnaire & Risk Analysis tab.
 *
 * State machine (drives badge + CTA + buttons):
 *
 *   mode = "AutoScoreOnly"  → CTA "Accept & Publish Auto-Score", never blocked on gap text
 *   mode = "SmeOnly"        → CTA "Save & Publish", blocked when any non-Compliant
 *                              finding has empty gap notes
 *   mode = "Both"           → CTA "Save & Publish", same gap-text blocker as SmeOnly
 *   mode = "None"           → no admin CTA; the VQQ wasn't requested with any
 *                              add-on, so there's nothing to publish
 *
 * State badge:
 *   - "Unpublished"           — never published
 *   - "Published by X · Y"    — currently published, no edits since
 *   - "Unpublished changes"   — was published but admin edited after; sponsor
 *                                 view stays pinned to the previously-published
 *                                 snapshot until the admin re-publishes
 *
 * Only renders when the current user is a Diligent admin. Sponsor + vendor
 * see no publish controls — their view comes from the published snapshot only.
 */
const PublishBar = ({
  mode,
  isPublished,
  publishedAt,
  publishedBy,
  hasUnpublishedChanges,
  missingGapTextCount,
  unscoredCount = 0,
  isPublishing,
  isUnpublishing,
  onPublish,
  onUnpublish,
}) => {
  const [confirmUnpublish, setConfirmUnpublish] = useState(false);

  const requireGapText = mode === "SmeOnly" || mode === "Both";
  // Questions still waiting for a score block first; gap text second.
  const blocker = publishBlocker({ unscoredCount, missingGapTextCount, requireGapText });
  const blocked = blocker !== null;
  const blockedByUnscored = blocker?.kind === "unscored";
  const ctaLabel =
    mode === "AutoScoreOnly" ? "Accept & Publish Auto-Score" : "Save & Publish";

  const stateLabel = hasUnpublishedChanges
    ? "Unpublished changes"
    : isPublished
      ? `Published by ${publishedBy ?? "Diligent admin"}${publishedAt ? ` · ${formatStamp(publishedAt)}` : ""}`
      : "Unpublished";
  const stateClass = hasUnpublishedChanges
    ? "bg-amber-50 text-amber-800 border-amber-200"
    : isPublished
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : "bg-gray-50 text-gray-700 border-gray-200";

  if (mode === "None") {
    return (
      <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
        This VQQ was not requested with Auto-Score or SME Score & Gap Analysis.
        No admin review workflow applies.
      </div>
    );
  }

  return (
    <div className="rounded-md border border-indigo-100 bg-indigo-50/40 px-3 py-2">
      <div className="flex flex-wrap items-center gap-3">
        <ModeBadge mode={mode} />
        <span
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${stateClass}`}
        >
          {stateLabel}
        </span>

        {blocked ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700">
            <ExclamationIcon className="h-3 w-3" />
            {blocker.label}
          </span>
        ) : null}

        <div className="ml-auto flex items-center gap-2">
          {isPublished ? (
            confirmUnpublish ? (
              <>
                <span className="text-[11px] text-gray-700">Unpublish?</span>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmUnpublish(false);
                    onUnpublish?.();
                  }}
                  disabled={isUnpublishing}
                  className="inline-flex items-center rounded border border-rose-300 bg-white px-2 py-0.5 text-[11px] font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                >
                  Confirm
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmUnpublish(false)}
                  className="text-[11px] text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmUnpublish(true)}
                className="inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Unpublish
              </button>
            )
          ) : null}
          {/*
            Publish CTA visibility:
              - Hide when the run is already published AND there are no
                edits since the last publish — nothing to push.
              - Show on first publish (isPublished=false).
              - Show as "Re-publish" when the admin has edited since the
                last successful publish (hasUnpublishedChanges=true).
          */}
          {(!isPublished || hasUnpublishedChanges) ? (
            <button
              type="button"
              onClick={onPublish}
              disabled={blocked || isPublishing}
              className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-semibold shadow-sm ${
                isPublishing || blockedByUnscored
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : blocked
                    // 14031 AC #17 — amber "Review Required" state swaps the CTA
                    // wording + palette when publish is blocked by missing gap
                    // text. Clicking it scrolls to the first blocking finding
                    // (FE handler in VqqResponsesReadOnly).
                    ? "bg-amber-500 text-white hover:bg-amber-600 cursor-pointer"
                    : "bg-indigo-600 text-white hover:bg-indigo-700"
              }`}
              title={
                blockedByUnscored
                  ? `${blocker.label}. Score every question before publishing.`
                  : blocked
                    ? `Add gap notes to ${missingGapTextCount} finding${missingGapTextCount === 1 ? "" : "s"} before publishing. Click to jump to the first one.`
                    : undefined
              }
            >
              {isPublishing
                ? "Publishing…"
                : blockedByUnscored
                  ? "Scoring incomplete"
                  : blocked
                    ? `Review Required — ${missingGapTextCount} finding${missingGapTextCount === 1 ? "" : "s"} need gap${missingGapTextCount === 1 ? "" : "s"}`
                    : isPublished && hasUnpublishedChanges
                      ? "Re-publish updates"
                      : ctaLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const MODE_LABEL = {
  AutoScoreOnly: "Auto-Score",
  SmeOnly: "SME Score & Gap Analysis",
  Both: "Auto-Score + SME",
};

const MODE_CLASS = {
  AutoScoreOnly: "bg-blue-100 text-blue-800 border-blue-200",
  SmeOnly: "bg-violet-100 text-violet-800 border-violet-200",
  Both: "bg-indigo-100 text-indigo-800 border-indigo-200",
};

const ModeBadge = ({ mode }) => (
  <span
    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
      MODE_CLASS[mode] || "bg-gray-100 text-gray-700 border-gray-200"
    }`}
  >
    {MODE_LABEL[mode] || mode}
  </span>
);

const ExclamationIcon = ({ className }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={className} aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a1 1 0 011 1v3a1 1 0 11-2 0V7a1 1 0 011-1zm0 8a1 1 0 100-2 1 1 0 000 2z"
      clipRule="evenodd"
    />
  </svg>
);

const formatStamp = (iso) => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

export default PublishBar;
